




































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { StationStatus, MelosStation } from '@/services/api.service';
import MelosPlayerController from '@/components/MelosPlayerController.vue';
import melosLogo from '@/assets/finalLogoMonochromo.svg';
import jamendoLogo from '@/assets/jamendo.svg';
import _ from 'lodash';

export enum MelosPlayerStatus {
  PLAYING = 'playing',
  PAUSED = 'paused',
  STOPPED = 'stopped',
}

@Component({
  components: {
    MelosPlayerController,
  },
})
export default class MelosPlayerInterface extends Vue {
  @Prop() private playerData!: any;
  private fullAudioSource: string = '';
  private targetTime: number = 0;
  private onHover: boolean = false;
  private debouncedWindowUpdate: any;
  private windowHeight: number = -1;
  private windowWidth: number = -1;

  private created() {
    this.debouncedWindowUpdate = _.debounce(this.onWindowSizeUpdated, 500);
    (window as any).addEventListener('resize', this.debouncedWindowUpdate);
    this.$store.commit('setWindowData', { height: (window as any).innerHeight, width: (window as any).innerWidth });
  }

  private destroyed() {
    (window as any).removeEventListener('resize', this.debouncedWindowUpdate);
  }

  @Watch('waveformContent')
  private onWaveformUpdated() {
    if (this.waveformContent === undefined || !this.showLargeInterface) {
      return;
    }
    const waveformContainer: any = this.$refs.waveformContainer as any;
    waveformContainer.innerHTML = this.waveformContent
    .replace(/preserveAspectRatio=".*"/, 'preserveAspectRatio="none"');
  }

  @Watch('showLargeInterface')
  private onLargeInterfaceVisibilityChanged() {
    setTimeout(() => this.onWaveformUpdated());
  }

  private onWindowSizeUpdated() {
    this.$store.commit('setWindowData', { height: (window as any).innerHeight, width: (window as any).innerWidth });
  }

  private get showLargeInterface(): boolean {
    const show = this.$route.path.includes('playing') || this.$store.state.windowData.height > 900;
    this.$store.commit('setInterfaceData', { showLargeInterface: show });
    return show;
  }

  private get providerLogo() {
    switch (this.playerData.provider) {
      case 'jamendo':
        return jamendoLogo;
      default:
        return melosLogo;
    }
  }

  private get melosStations(): MelosStation[] {
    return this.$store.state.melosStations;
  }

  private get waveformContent(): string | undefined {
    return this.$store.state.player?.waveformContent;
  }

  private get playerStatus(): MelosPlayerStatus {
    return this.$store.state.player?.currentStatus;
  }

  private getFormattedTime(time: number): string {
    const minutes = String(Math.floor(time / 60)).padStart(2, '0');
    const seconds = String(Math.floor(time % 60)).padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  private get secondaryOverlayWidth(): number {
    const documentObj: any = document as any;
    const isTactile: boolean = 'ontouchstart' in documentObj.documentElement;
    const percentage: number = (this.onHover && !isTactile) ? this.targetPlayedPercentage : this.playedPercentage;
    return 100 - percentage;
  }

  private onPlayerMouseUpdate(event: any) {
    const audioContainer: any = this.$refs.melosAudioContainer as any;
    const containerWidth: number = audioContainer?.offsetWidth || 1;
    const containerX: number = audioContainer.getBoundingClientRect()?.left;
    const offset: number = (event?.clientX || 0) - (containerX || 0) ;
    const newTime: number = (offset / containerWidth) * this.trackDuration;
    this.targetTime = newTime;
  }

  private onPlayerClick(event: any) {
    const audioContainer: any = this.$refs.melosAudioContainer as any;
    const containerWidth: number = audioContainer?.offsetWidth || 1;
    const containerX: number = audioContainer.getBoundingClientRect()?.left;
    const offset: number = (event?.clientX || 0) - (containerX || 0) ;
    const newTime: number = (offset / containerWidth) * this.trackDuration;
    this.$store.commit('setPlayerData', { targetElapsedTime: newTime });
  }

  private get targetPlayedPercentage(): number {
    if (this.trackDuration === 0) {
      return 0;
    }
    return 100 * this.targetTime / this.trackDuration;
  }

  private get playerTime(): number {
    return this.$store.state.player?.currentElapsedTime || 0;
  }

  private get trackDuration(): number {
    return this.$store.state.player?.trackDuration || 0;
  }

  private get playedPercentage(): number {
    if (this.trackDuration === 0) {
      return 0;
    }
    return 100 * this.playerTime / this.trackDuration;
  }

  private togglePlayState() {
    let playerStatus: MelosPlayerStatus = MelosPlayerStatus.PAUSED;
    if (this.playerStatus !== MelosPlayerStatus.PLAYING) {
      playerStatus = MelosPlayerStatus.PLAYING;
    }
    this.$store.commit('setPlayerData', { targetStatus: playerStatus });
  }

  private toggleLoopState() {
    this.$store.commit('setPlayerData', { loop: !this.$store.state.player?.loop });
  }

  private getCurrentStationIndex(): number {
    const stationId = this.$store.state.player.stationId;
    if (stationId === undefined) {
      return -1;
    }

    return this.melosStations.findIndex((station) => station._id === stationId);
  }

  private goForward() {
    const currentIndex: number = this.getCurrentStationIndex();

    if (currentIndex === -1) {
      return;
    }
    const newIndex = (currentIndex + 1) % this.melosStations.length;
    this.$store.commit('setPlayerData', {
      targetStatus: MelosPlayerStatus.PLAYING,
      stationId: this.melosStations[newIndex]._id,
    });
  }

  private openLink() {
    if (this.playerData.link === undefined) {
      return;
    }

    (window as any).open(this.playerData.link, '_blank');
  }

  private goBack() {
    const currentIndex: number = this.getCurrentStationIndex();

    if (currentIndex === -1) {
      return;
    }
    const newIndex = currentIndex === 0 ? this.melosStations.length - 1 : currentIndex - 1;
    this.$store.commit('setPlayerData', {
      targetStatus: MelosPlayerStatus.PLAYING,
      stationId: this.melosStations[newIndex]._id,
    });
  }

  private togglePlayingView() {
    if (this.$route.name === 'playing') {
      this.$router.go(-1);
    } else {
      this.$router.push('/playing');
    }
  }
}
